import { useLocation } from 'wouter';
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export function EmailConfirmed() {
  const [, setLocation] = useLocation();
  
  // Get URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const success = urlParams.get('success');
  const error = urlParams.get('error');

  const handleContinue = () => {
    setLocation('/user');
  };

  const handleResendConfirmation = async () => {
    try {
      const response = await fetch('/api/auth/resend-confirmation', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('sb-access-token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        alert('Confirmation email sent! Please check your inbox.');
      } else {
        alert('Failed to resend confirmation email. Please try again.');
      }
    } catch (error) {
      alert('Error sending confirmation email. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
        {success === 'true' ? (
          <>
            {/* Success State */}
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Email Confirmed! 🎉
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Your email has been successfully verified. You can now access all Prize Drop features and start entering competitions!
              </p>
            </div>

            <div className="space-y-4">
              <Button 
                onClick={handleContinue}
                className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                data-testid="button-continue"
              >
                Continue to Dashboard
              </Button>
            </div>
          </>
        ) : error ? (
          <>
            {/* Error State */}
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-4">
                <XCircle className="w-8 h-8 text-red-600 dark:text-red-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Confirmation Failed
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                {error === 'invalid_token' 
                  ? 'The confirmation link is invalid or has expired.'
                  : error === 'server_error'
                  ? 'A server error occurred. Please try again.'
                  : 'There was an issue confirming your email address.'}
              </p>
            </div>

            <div className="space-y-3">
              <Button 
                onClick={handleResendConfirmation}
                className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                data-testid="button-resend"
              >
                Resend Confirmation Email
              </Button>
              <Button 
                variant="outline"
                onClick={handleContinue}
                className="w-full"
                data-testid="button-continue-anyway"
              >
                Continue Anyway
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Processing/Loading State */}
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                <AlertCircle className="w-8 h-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                Processing Confirmation
              </h1>
              <p className="text-gray-600 dark:text-gray-300">
                Please wait while we process your email confirmation...
              </p>
            </div>

            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          </>
        )}

        {/* Additional Information */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
            Need help?
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Contact support at{' '}
            <a 
              href="mailto:support@prizedrop.co.za" 
              className="text-blue-600 dark:text-blue-400 hover:underline"
            >
              support@prizedrop.co.za
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}